import { useContext } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTheme } from 'styled-components';
import { DropAreaContainer } from './styles';
import { TextS } from 'components/Text';
import { Button, Flex } from 'antd';
import { MdOutlineUploadFile } from 'react-icons/md';
import UploadFileIcon from 'components/Icons/UploadFile';
import { FileInfo } from './FileInfo';
import UploadPercentage from './UploadPercentage';
import { UploadCsvProviderContext } from './ImportCsvModal';

export interface IDropAreaProps {
	file?: File | null;
	loading?: boolean;
	loadPercentage?: number;
	onUpload: (file: File) => void;
	removeFile: () => void;
	onNext: () => void;
}

const DropArea = ({
	file,
	loading,
	loadPercentage,
	onUpload,
	removeFile,
	onNext,
}: IDropAreaProps) => {
	const theme = useTheme();
	const { onClose } = useContext(UploadCsvProviderContext);

	const onDrop = (files: File[]) => {
		if (files.length > 0) {
			onUpload(files[0]);
		}
	};

	const handleRemoveFile = () => {
		removeFile();
	};

	const handleNext = () => {
		if (loading || !file) return;
		onNext();
	};

	const { getRootProps, getInputProps, isDragReject, isDragActive } =
		useDropzone({
			onDrop,
			accept: {
				'text/csv': ['.csv'],
				'application/vnd.ms-excel': ['.csv'],
			},
		});

	return (
		<Flex
			style={{
				flexDirection: 'column',
				maxHeight: '80vh',
				overflow: 'auto',
				width: '100%',
			}}
		>
			{loading ? (
				<DropAreaContainer>
					<div
						style={{
							minHeight: '9rem',
							width: '100%',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
						}}
					>
						<UploadPercentage loadingPercentage={loadPercentage} />
					</div>
				</DropAreaContainer>
			) : file ? (
				<FileInfo file={file} onRemove={handleRemoveFile} />
			) : (
				<DropAreaContainer {...getRootProps()}>
					<input {...getInputProps()} />
					<UploadFileIcon />
					<TextS
						style={{
							textAlign: 'center',
							maxWidth: '300px',
						}}
					>
						{isDragReject
							? 'Arquivo inválido'
							: 'Arraste seu arquivo (informar a extensão dos arquivos aceitos), ou clique no botão abaixo para fazer o upload do seu arquivo.'}
					</TextS>
					<Flex style={{ margin: '1rem 0' }}>
						<Button type="primary" style={{ padding: '0.8rem' }}>
							<TextS
								weight="bold"
								style={{
									color: theme.white,
								}}
							>
								{isDragActive ? 'Solte aqui' : 'Fazer upload'}
							</TextS>
							<MdOutlineUploadFile
								size={18}
								style={{
									color: theme.white,
								}}
							/>
						</Button>
					</Flex>
				</DropAreaContainer>
			)}

			<Flex
				gap={10}
				style={{
					flexDirection: 'column',
					margin: '1rem 0',
					width: '100%',
				}}
			>
				<Button
					style={{ width: '100%' }}
					type="primary"
					onClick={handleNext}
				>
					<TextS weight={600} style={{ color: theme.white }}>
						Finalizar
					</TextS>
				</Button>
				<Button
					onClick={() => {
						if (loading) return;
						handleRemoveFile();
						onClose?.();
					}}
					style={{
						width: '100%',
						outline: 'none',
						border: 'none',
					}}
					type="default"
				>
					<TextS weight={600} style={{ color: theme.primary }}>
						Cancelar
					</TextS>
				</Button>
			</Flex>
		</Flex>
	);
};

export default DropArea;
